import { useRef } from "react";

const elevenlabsApiKey = process.env.REACT_APP_ELEVENLABS_API_KEY;
const elevenlabsVoiceId = process.env.REACT_APP_ELEVENLABS_VOICE_ID;

export function useMicMonitor(onUserSpeak: () => void) {
  const audioContextRef = useRef<AudioContext | null>(null);
  const micStreamRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationFrameIdRef = useRef<number | null>(null);
  const monitorStartTimeRef = useRef<number>(0);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const audioContext = new AudioContext();
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 512;

      source.connect(analyser);
      const data = new Uint8Array(analyser.frequencyBinCount);
      monitorStartTimeRef.current = performance.now();

      const checkVolume = () => {
        analyser.getByteFrequencyData(data);
        const avg = data.reduce((sum, v) => sum + v, 0) / data.length;
        const now = performance.now();
        const gracePeriod = 500;

        if (now - monitorStartTimeRef.current > gracePeriod) {
          if (avg > 25) {
            onUserSpeak();
            return;
          }
        }

        animationFrameIdRef.current = requestAnimationFrame(checkVolume);
      };

      checkVolume();

      audioContextRef.current = audioContext;
      micStreamRef.current = source;
      analyserRef.current = analyser;
    } catch (err) {
      console.warn("Mic monitor failed:", err);
    }
  };

  const stop = () => {
    if (animationFrameIdRef.current) {
      cancelAnimationFrame(animationFrameIdRef.current);
      animationFrameIdRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    micStreamRef.current = null;
    analyserRef.current = null;
  };

  return { start, stop };
}

export async function elevenLabsTTSStream(
  text: string,
  abortSignal?: AbortSignal
): Promise<ReadableStream<Uint8Array>> {
  const res = await fetch(
    `https://api.elevenlabs.io/v1/text-to-speech/${elevenlabsVoiceId}/stream?output_format=mp3_44100_128&optimize_streaming_latency=2`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "xi-api-key": elevenlabsApiKey || "",
      },
      body: JSON.stringify({
        text,
        // model_id: "eleven_multilingual_v2",
        model_id: "eleven_flash_v2_5",
      }),
      signal: abortSignal,
    }
  );
  if (!res.ok || !res.body) throw new Error("TTS stream failed");
  return res.body as ReadableStream<Uint8Array>;
}

// Simple STT using Web Speech API, returns transcript as Promise
export function speechToText(): Promise<string> {
  return new Promise((resolve, reject) => {
    const SpeechRecognition =
      (window as any).SpeechRecognition ||
      (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) {
      reject(new Error("Web Speech API not supported"));
      return;
    }
    const recognition = new SpeechRecognition();
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.lang = "en-US";
    recognition.onresult = (event: any) => {
      const transcript = Array.from(event.results)
        .map((r: any) => r[0].transcript)
        .join(" ");
      resolve(transcript);
    };
    recognition.onerror = (event: any) => {
      reject(event.error);
    };
    recognition.start();
  });
}
